import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Loader2, Pencil, Users, X, TrendingUp, Clock } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Admin panel — lists every active staking position across all users and
 * lets the admin change the APR of any individual position. When the APR
 * changes, the backend credits the reward accrued so far at the OLD rate,
 * bumps the principal, and resets `started_at` for the NEW rate.
 */
export default function StakingPositionsPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {id, apr_pct}
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/staking-positions");
      setRows(r.data.positions || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load positions");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!editing) return;
    const n = Number(editing.apr_pct);
    if (!Number.isFinite(n) || n < 0 || n > 1000) {
      toast.error("APR must be between 0 and 1000%");
      return;
    }
    setBusy(true);
    try {
      const r = await api.put(`/admin/staking-positions/${editing.id}/apr`, { apr: n / 100 });
      toast.success(
        `APR updated · credited ${r.data.credited_reward.toFixed(8)} ${editing.asset}`
      );
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-staking-positions-panel" className="border border-[#2A2E38] bg-[#141720] p-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#6E7587] flex items-center gap-2">
            <Users size={11} /> Active positions
          </div>
          <h3 className="font-display text-lg font-bold mt-1">Per-user staking APR</h3>
          <p className="text-xs text-[#A3A9B8] mt-1 max-w-2xl">
            Each row is one live staking contract. Changing the APR credits the
            reward accrued so far at the old rate (added to the principal), then
            resets the 24 h clock. Reward accrual now ticks{" "}
            <strong className="text-[#E8E2D9]">once every 24 hours</strong>.
          </p>
        </div>
        <button
          data-testid="admin-positions-refresh"
          onClick={load}
          disabled={loading}
          className="text-xs px-3 py-1.5 border border-[#2A2E38] rounded-sm hover:border-[#7C5CFF] hover:text-[#7C5CFF] inline-flex items-center gap-1"
        >
          {loading ? <Loader2 size={12} className="animate-spin" /> : <TrendingUp size={12} />}
          Refresh
        </button>
      </div>

      <div className="mt-5 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] tracking-[0.25em] uppercase text-[#6E7587] border-b border-[#2A2E38]">
              <th className="px-3 py-2 text-left">User</th>
              <th className="px-3 py-2 text-left">Asset</th>
              <th className="px-3 py-2 text-right">Principal</th>
              <th className="px-3 py-2 text-right">APR</th>
              <th className="px-3 py-2 text-right">Accrued</th>
              <th className="px-3 py-2 text-right">Next tick</th>
              <th className="px-3 py-2 text-right">Started</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="p-6 text-center text-[#6E7587]">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={8} className="p-8 text-center text-[#6E7587]">No active staking positions</td></tr>
            )}
            {!loading && rows.map((p) => {
              const started = p.started_at ? new Date(p.started_at) : null;
              const next = p.next_reward_at ? new Date(p.next_reward_at) : null;
              return (
                <tr key={p.id} data-testid={`position-row-${p.id}`} className="border-b border-[#2A2E38] last:border-0 hover:bg-[#0B0D12]/50">
                  <td className="px-3 py-3">
                    <div className="text-[#F4F1EB]">{p.user?.full_name || "—"}</div>
                    <div className="text-[10px] text-[#6E7587] font-mono">{p.user?.email}</div>
                  </td>
                  <td className="px-3 py-3 font-mono text-[#F4F1EB] font-medium">{p.asset}</td>
                  <td className="px-3 py-3 text-right font-mono">{Number(p.principal).toFixed(8)}</td>
                  <td className="px-3 py-3 text-right font-mono text-[#7C5CFF]">{(p.apr * 100).toFixed(4)}%</td>
                  <td className="px-3 py-3 text-right font-mono text-[#7C9C69]">
                    {Number(p.accrued_reward).toFixed(8)}
                  </td>
                  <td className="px-3 py-3 text-right text-xs text-[#A3A9B8]">
                    <div className="inline-flex items-center gap-1"><Clock size={10} />{next ? next.toLocaleString() : "—"}</div>
                  </td>
                  <td className="px-3 py-3 text-right text-xs text-[#6E7587]">
                    {started ? started.toLocaleDateString() : "—"}
                  </td>
                  <td className="px-3 py-3 text-right">
                    <button
                      data-testid={`position-edit-${p.id}`}
                      onClick={() => setEditing({ id: p.id, apr_pct: (p.apr * 100).toFixed(4), asset: p.asset, principal: p.principal, user_email: p.user?.email })}
                      className="text-xs px-2.5 py-1.5 border border-[#2A2E38] text-[#A3A9B8] rounded-sm hover:border-[#7C5CFF] hover:text-[#7C5CFF] inline-flex items-center gap-1"
                    >
                      <Pencil size={11} /> APR
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editing && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          onClick={() => !busy && setEditing(null)}
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          <div
            data-testid="position-edit-modal"
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md bg-[#0B0D12] border border-[#2A2E38] p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#7C5CFF]">Position APR</div>
                <h3 className="font-display text-xl font-bold mt-1">Change APR</h3>
                <div className="text-xs text-[#A3A9B8] mt-1">
                  {editing.principal} {editing.asset} · {editing.user_email}
                </div>
              </div>
              <button
                onClick={() => setEditing(null)}
                disabled={busy}
                className="p-2 text-[#6E7587] hover:text-[#7C5CFF] disabled:opacity-50"
              >
                <X size={16} />
              </button>
            </div>

            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase text-[#A3A9B8]">New APR (%)</label>
              <input
                data-testid="position-edit-input"
                type="number"
                step="0.01"
                value={editing.apr_pct}
                onChange={(e) => setEditing({ ...editing, apr_pct: e.target.value })}
                autoFocus
                className="mt-1 w-full bg-[#141720] border border-[#2A2E38] rounded-sm px-3 py-2 font-mono text-lg text-right focus:border-[#7C5CFF] focus:outline-none"
              />
              <div className="text-xs text-[#6E7587] mt-2">
                Reward accrued so far will be credited to the principal, then
                the 24 h clock resets.
              </div>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={() => setEditing(null)}
                disabled={busy}
                className="px-4 py-2 text-sm border border-[#2A2E38] hover:border-[#C75C5C] hover:text-[#C75C5C] disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                data-testid="position-edit-save"
                onClick={save}
                disabled={busy}
                className="px-4 py-2 text-sm bg-[#7C5CFF] text-white hover:bg-[#9C82FF] disabled:opacity-50 inline-flex items-center gap-2"
              >
                {busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                {busy ? "Saving…" : "Save new APR"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
